import { useEffect, useState } from "react";
import api from "../constant/api";
import { motion, AnimatePresence } from "framer-motion";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF";
import { useMenuPermission } from "../hooks/useMenuPermission";

export default function Invoice() {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [tempSearch, setTempSearch] = useState(""); // mobile filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  const [fullInvoice, setFullInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");

  const itemsPerPage = 5;

   const MENU_ID = 7; 
    const { permissions, loading } = useMenuPermission(MENU_ID);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const res = await api.get("/order/getAllInvoices");
      setInvoices(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  // 🔍 Filter Logic (Based on Status & Customer Name)
  const filteredInvoices = invoices
    .filter((inv) => {
      if (statusFilter === "Pending") {
        return !inv.status || inv.status === "Pending";
      }
      if (statusFilter !== "All") {
        return inv.status === statusFilter;
      }
      return true;
    })
    .filter((inv) =>
      inv.customer_name?.toLowerCase().includes(search.toLowerCase())
    );

  // 📄 Pagination Logic
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  // 👁 Fetch Single Invoice Details for Modal View
  const openInvoiceDetails = async (invoice_id) => {
    try {
      const res = await api.get(`/order/getInvoice/${invoice_id}`);
      setFullInvoice(res.data); // backend keys dynamically matches modal view below
    } catch (err) {
      console.log(err);
    }
  };

   if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 font-medium">Checking permissions...</p>
      </div>
    );
  }

  // 📄 PDF Print Method
  const handleInvoicePrint = async (invoice_id) => {
    try {
      const res = await api.get(`/order/getInvoice/${invoice_id}`);
      console.log('invoice',res.data.invoice)
      const blob = await pdf(
        <InvoicePDF invoice={res.data.invoice} items={res.data.items} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (err) {
      console.log(err);
    }
  };

  // 📄 Pagination Helper Array
  const getPagination = (curr, total) => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (curr > 3) pages.push("...");
      let start = Math.max(2, curr - 1);
      let end = Math.min(total - 1, curr + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (curr < total - 2) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Invoice List</h1>
        <button
          className="md:hidden bg-gray-800 text-white px-3 py-2 rounded"
          onClick={() => setShowFilter(true)}
        >
          Filter ⚙️
        </button>
      </div>

      {/* DESKTOP SEARCH */}
      <div className="hidden md:flex gap-3 mb-4">
        <input
          type="text"
          placeholder="Search by customer name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="border px-3 py-2 rounded-xl w-64 shadow-sm"
        />
      </div>

      {/* ========================= */}
      {/* DESKTOP VIEW */}
      {/* ========================= */}
      <div className="hidden md:block overflow-x-auto">
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200 p-4">
          
          {/* STATS TOP CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div
              onClick={() => setStatusFilter("All")}
              className={`bg-gradient-to-r from-blue-600 to-blue-400 text-white p-5 rounded-2xl shadow-lg cursor-pointer transition hover:scale-105 ${statusFilter === "All" ? "ring-4 ring-blue-200" : ""}`}
            >
              <p className="text-sm opacity-80">Total Invoices</p>
              <h2 className="text-2xl font-bold mt-1">{invoices.length}</h2>
            </div>

            <div
              onClick={() => setStatusFilter("Paid")}
              className={`bg-gradient-to-r from-green-600 to-green-400 text-white p-5 rounded-2xl shadow-lg cursor-pointer transition hover:scale-105 ${statusFilter === "Paid" ? "ring-4 ring-green-200" : ""}`}
            >
              <p className="text-sm opacity-80">Paid Invoices</p>
              <h2 className="text-2xl font-bold mt-1">
                {invoices.filter((i) => i.status === "Paid").length}
              </h2>
            </div>

            <div
              onClick={() => setStatusFilter("Pending")}
              className={`bg-gradient-to-r from-yellow-500 to-yellow-300 text-white p-5 rounded-2xl shadow-lg cursor-pointer transition hover:scale-105 ${statusFilter === "Pending" ? "ring-4 ring-yellow-200" : ""}`}
            >
              <p className="text-sm opacity-80">Pending Invoices</p>
              <h2 className="text-2xl font-bold mt-1">
                {invoices.filter((i) => !i.status || i.status === "Pending").length}
              </h2>
            </div>
          </div>

          {/* TABLE CONTAINER */}
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-gray-900 to-gray-700 text-white">
              <tr>
                <th className="p-4 text-left"># ID</th>
                <th className="p-4 text-left">Invoice No</th>
                <th className="p-4 text-left">Customer</th>
                <th className="p-4 text-left">Total</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentInvoices.map((item) => (
                <tr key={item.invoice_id} className="border-b hover:bg-blue-50/40 transition duration-300">
                  <td className="p-4 font-semibold text-gray-700">#{item.invoice_id}</td>
                  <td className="p-4 font-medium text-gray-800">{item.invoice_no}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center font-bold shadow">
                        {item.customer_name?.charAt(0) || "C"}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{item.customer_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 font-bold text-gray-800">₹{item.net_total}</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.status === "Paid" ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>
                      {item.status || "Pending"}
                    </span>
                  </td>
                  <td className="p-4 text-gray-400 text-sm">{item.created_at?.slice(0, 10)}</td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => openInvoiceDetails(item.invoice_id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs shadow transition"
                      >
                        View
                      </button>

                      {permissions?.canPdf && (
                      <button
                        onClick={() => handleInvoicePrint(item.invoice_id)}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-lg text-xs shadow transition"
                      >
                        PDF
                      </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================= */}
      {/* MOBILE UI */}
      {/* ========================= */}
      <div className="md:hidden flex flex-col gap-4 px-2">
        {currentInvoices.map((item, i) => (
          <div key={i} className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl shadow-lg p-4">
            <span className={`absolute top-3 right-3 text-[10px] px-2 py-1 rounded-full font-semibold ${item.status === "Paid" ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>
              {item.status || "Pending"}
            </span>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center font-bold text-lg shadow">
                {item.customer_name?.charAt(0) || "C"}
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-sm">{item.customer_name}</h3>
                <p className="text-xs text-gray-400">Inv No: {item.invoice_no}</p>
                <p className="text-xs text-gray-500 font-bold mt-0.5">₹{item.net_total}</p>
              </div>
            </div>

            <div className="mt-3 flex justify-between items-center text-xs text-gray-400">
              <p>ID: #{item.invoice_id}</p>
              <p>{item.created_at?.slice(0, 10)}</p>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => openInvoiceDetails(item.invoice_id)}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-400 text-white py-2 rounded-xl text-center text-sm shadow"
              >
                👁 View
              </button>
                 {permissions?.canPdf && (
              <button
                onClick={() => handleInvoicePrint(item.invoice_id)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-xl text-center text-sm shadow"
              >
                📄 PDF
              </button>
                )}
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION PANEL */}
      <div className="flex justify-center items-center mt-6 gap-2 flex-wrap">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg border bg-white disabled:opacity-50"
        >
          ◀
        </button>
        {getPagination(currentPage, totalPages).map((page, idx) =>
          page === "..." ? (
            <span key={idx} className="px-2">...</span>
          ) : (
            <button
              key={idx}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-2 rounded-lg border transition ${currentPage === page ? "bg-blue-500 text-white" : "bg-white hover:bg-gray-100"}`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg border bg-white disabled:opacity-50"
        >
          ▶
        </button>
      </div>

      {/* MOBILE DRAWER FILTER */}
      <AnimatePresence>
        {showFilter && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
              onClick={() => setShowFilter(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="fixed bottom-0 left-0 w-full bg-white p-4 rounded-t-2xl z-[9999]"
            >
              <input
                placeholder="Search Client Name"
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                className="border w-full mb-3 p-2 rounded-xl"
              />
              <button
                onClick={() => {
                  setSearch(tempSearch);
                  setShowFilter(false);
                  setCurrentPage(1);
                }}
                className="bg-blue-500 text-white w-full py-2 rounded-xl font-bold shadow animate-pulse"
              >
                Apply Filters
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ========================= */}
      {/* INVOICE DETAILS MODAL PANEL */}
      {/* ========================= */}
      <AnimatePresence>
        {fullInvoice && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFullInvoice(null)}
          >
            <motion.div
              className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* MODAL HEADER */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white p-5 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Invoice Details</h2>
                  <p className="text-sm opacity-80">No: {fullInvoice?.invoice?.invoice_no}</p>
                </div>
                <button onClick={() => setFullInvoice(null)} className="text-white text-xl font-bold hover:scale-110 transition">
                  ✖
                </button>
              </div>

              {/* MODAL BODY */}
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Customer</p>
                    <p className="font-semibold">{fullInvoice?.invoice?.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Date</p>
                    <p className="font-semibold">{fullInvoice?.invoice?.created_at?.slice(0, 10)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Net total</p>
                    <p className="font-bold text-blue-600">₹{fullInvoice?.invoice?.net_total}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <span className={`px-2 py-0.5 text-xs rounded font-semibold ${fullInvoice?.invoice?.status === "Paid" ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>
                      {fullInvoice?.invoice?.status || "Pending"}
                    </span>
                  </div>
                </div>

                {/* ITEMS LIST */}
                <h3 className="font-bold text-gray-800 border-b pb-1">Billed Items</h3>
                <div className="space-y-2">
                  {fullInvoice?.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-gray-50/50 p-3 rounded-xl border border-gray-100 text-sm">
                      <div>
                        <p className="font-semibold text-gray-800">{item.product_name}</p>
                        <p className="text-xs text-gray-400">Qty / Box: {item.box || item.qty || 0}</p>
                      </div>
                      <p className="font-bold text-gray-700">₹{Number(item.item_amount).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex justify-end">
                  <button
                    onClick={() => handleInvoicePrint(fullInvoice?.invoice?.invoice_id)}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-xl shadow-md transition text-sm font-medium"
                  >
                    📄 Open Invoice PDF
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}